import { useState, useRef, useEffect } from 'react';
import { Settings, CircleAlert, ArrowLeft, ArrowRight, Save, ChevronDown, Search, X } from 'lucide-react';
import { getOrganizationTypes, validateLicense, createOrganization } from '../../services/PlatformService/platformOrganizationService';
import { toast } from 'react-toastify';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import './styles/PlatformConfiguration.css';

/* ------------------------------------------------------------------ */
/*  Multi Language Select                                              */
/* ------------------------------------------------------------------ */

interface Language {
  code: string;
  label: string;
}

const ALL_LANGUAGES: Language[] = [
  { code: 'en', label: 'English' },
  { code: 'ar', label: 'Arabic' },
  { code: 'fr', label: 'French' },
  { code: 'es', label: 'Spanish' },
  { code: 'de', label: 'German' },
  { code: 'zh', label: 'Chinese' },
  { code: 'hi', label: 'Hindi' },
  { code: 'pt', label: 'Portuguese' },
  { code: 'ru', label: 'Russian' },
  { code: 'ja', label: 'Japanese' },
];

const VISIBLE_PILL_COUNT = 3;

interface MultiLanguageSelectProps {
  value: string[];
  onChange: (codes: string[]) => void;
}

const MultiLanguageSelect = ({ value, onChange }: MultiLanguageSelectProps) => {
  const selected = value;
  const setSelected = (updater: string[] | ((prev: string[]) => string[])) => {
    const next = typeof updater === 'function' ? (updater as (prev: string[]) => string[])(selected) : updater;
    onChange(next);
  };

  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState('');
  const containerRef = useRef<HTMLDivElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
        setQuery('');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    if (isOpen) {
      searchRef.current?.focus();
    }
  }, [isOpen]);

  const toggleLanguage = (code: string) => {
    setSelected((prev) => (prev.includes(code) ? prev.filter((c) => c !== code) : [...prev, code]));
  };

  const removePill = (code: string, e: React.MouseEvent) => {
    e.stopPropagation();
    setSelected((prev) => prev.filter((c) => c !== code));
  };

  const handleClearAll = () => {
    setSelected([]);
    setIsOpen(false);
    setQuery('');
  };

  const handleApply = () => {
    setIsOpen(false);
    setQuery('');
  };

  const selectedLanguages = ALL_LANGUAGES.filter((l) => selected.includes(l.code));
  const visiblePills = selectedLanguages.slice(0, VISIBLE_PILL_COUNT);
  const overflowCount = selectedLanguages.length - visiblePills.length;

  const filteredLanguages = ALL_LANGUAGES.filter((l) => l.label.toLowerCase().includes(query.toLowerCase()));

  return (
    <div className="w-full max-w-md font-['Poppins']">
      <label className="block text-sm  text-[#1f2937] mb-2">Multi Language</label>

      <div ref={containerRef} className="relative">
        {/* Trigger */}
        <div
          onClick={() => setIsOpen((prev) => !prev)}
          className="flex items-center justify-between gap-2 w-full min-h-[42px] px-3 py-2 bg-white border border-[#e2e5ea] rounded-xl cursor-pointer transition-colors hover:border-[#25a88e]"
        >
          <div className="flex flex-wrap items-center gap-1.5 flex-1">
            {visiblePills.length === 0 && <span className="text-sm text-[#9ca3af] px-1">Select languages</span>}

            {visiblePills.map((lang) => (
              <span
                key={lang.code}
                className="flex items-center gap-1 pl-3 pr-2 py-1 bg-[#25a88e] text-white text-xs font-semibold rounded-full"
              >
                {lang.label}
                <button
                  type="button"
                  onClick={(e) => removePill(lang.code, e)}
                  className="hover:bg-[#25a88e]/10 rounded-full p-0.5 transition-colors"
                >
                  <X size={11} strokeWidth={3} />
                </button>
              </span>
            ))}

            {overflowCount > 0 && (
              <span className="px-2.5 py-1 bg-[#f3f4f6] text-[#4b5563] text-xs font-semibold rounded-full border border-[#e5e7eb]">
                +{overflowCount} more
              </span>
            )}
          </div>

          <ChevronDown size={18} className={`text-[#6b7280] shrink-0 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} />
        </div>

        {/* Dropdown */}
        {isOpen && (
          <div className="absolute z-20 mt-2 w-full bg-white border border-[#e2e5ea] rounded-xl shadow-lg overflow-hidden">
            {/* Search field */}
            <div className="relative p-2 border-b border-[#eef0f3]">
              <Search size={15} className="absolute left-5 top-1/2 -translate-y-1/2 text-[#9ca3af]" />
              <input
                ref={searchRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search language..."
                className="w-full pl-8 pr-3 py-2 text-sm bg-[#f9fafb] border border-transparent rounded-lg outline-none focus:border-[#25a88e] focus:bg-white transition-colors"
              />
            </div>

            {/* Options */}
            <div className="max-h-56 overflow-y-auto py-1">
              {filteredLanguages.length === 0 && <p className="px-4 py-3 text-sm text-[#9ca3af] text-center">No languages found</p>}

              {filteredLanguages.map((lang) => {
                const isChecked = selected.includes(lang.code);
                return (
                  <div
                    key={lang.code}
                    onClick={() => toggleLanguage(lang.code)}
                    className="flex items-center gap-3 px-4 py-2 text-sm cursor-pointer hover:bg-[#f3faf8] transition-colors"
                  >
                    <div
                      className={`flex items-center justify-center w-4 h-4 rounded border transition-colors ${
                        isChecked ? 'bg-[#25a88e] border-[#25a88e]' : 'border-[#d1d5db]'
                      }`}
                    >
                      {isChecked && (
                        <svg width="10" height="8" viewBox="0 0 10 8" fill="none">
                          <path d="M1 4L3.5 6.5L9 1" stroke="white" strokeWidth="1.6" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </div>
                    <span className={isChecked ? 'text-[#111827] font-medium' : 'text-[#374151]'}>{lang.label}</span>
                  </div>
                );
              })}
            </div>

            {/* Footer actions */}
            <div className="flex items-center justify-between gap-2 px-3 py-2.5 border-t border-[#eef0f3] bg-[#fafbfc]">
              <button
                type="button"
                onClick={handleClearAll}
                className="text-xs font-semibold text-[#6b7280] hover:text-[#ef4444] transition-colors px-2 py-1"
              >
                Clear all
              </button>

              <button
                type="button"
                onClick={handleApply}
                className="px-4 py-1.5 bg-[#25a88e] text-white text-xs font-semibold rounded-lg hover:bg-[#1e8f78] transition-colors"
              >
                Apply
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Organization Form                                                  */
/* ------------------------------------------------------------------ */

interface OrganizationFormProps {
  nextStep: () => void;
  previousStep: () => void;
  onCreated: (id: string) => void;
}

const licenseOptions = ['LIC-2026-001', 'LIC-2026-002', 'LIC-2026-003'];

const OrganizationForm = ({ nextStep, previousStep, onCreated }: OrganizationFormProps) => {
  const [organizationTypes, setOrganizationTypes] = useState<any[]>([]);
  const [selectedType, setSelectedType] = useState('');
  //const [organizationName, setOrganizationName] = useState('');
  const [licenseNumber, setLicenseNumber] = useState('');

  const organizationNameRef = useRef<HTMLInputElement>(null);
  const licenseNumberRef = useRef<HTMLSelectElement>(null);

  useEffect(() => {
    loadOrganizationTypes();
  }, []);

  const formik = useFormik({
    initialValues: {
      organizationName: '',
      licenseNumber: '',
      languages: ['en'] as string[],
    },

    validationSchema: Yup.object({
      organizationName: Yup.string().required('Organization Name is required'),

      licenseNumber: Yup.string().required('License Number is required'),
    }),

    onSubmit: async () => {
      await handleCreateOrganization();
    },
  });

  const loadOrganizationTypes = async () => {
    try {
      const data = await getOrganizationTypes();

      setOrganizationTypes(data);
    } catch (error) {
      console.error(error);
    }
  };

  const handleValidateLicense = async () => {
    try {
      const response = await validateLicense({
        licenseKey: licenseNumber,
      });
    } catch (error) {
      console.error(error);
    }
  };

  const handleCreateOrganization = async () => {
    try {
      // Validate the license first
      await validateLicense({
        licenseKey: formik.values.licenseNumber,
      });

      // Then create the organization
      const payload = {
        title: formik.values.organizationName,
        description: '',
        parentOrganizationId: null,
        organizationTypeId: selectedType,
        licenseKey: formik.values.licenseNumber,
        vision: '',
        mission: '',
        employeeCount: 0,
        branchCount: 0,
        establishDate: '',
        phone: '',
        email: '',
        address: '',
        website: '',
        sourceLanguageId: null,
        languages: formik.values.languages,
      };

      const response = await createOrganization(payload);

      toast.success('Organization created successfully');

      onCreated(response.id);

      nextStep();
    } catch (error) {
      console.error(error);

      toast.error('Invalid license or organization creation failed.');
    }
  };

  return (
    <form className="organization-wrapper" onSubmit={formik.handleSubmit}>
      {/* Header */}
      <div className="organization-inner">
        <div className="organization-header">
          <div className="organization-icon">
            <Settings className="organization-icon-svg" />
          </div>

          <div className="organization-text">
            <h2 className="organization-title">Organization</h2>

            <p className="organization-subtitle">Provide basic information about your organization.</p>
          </div>
        </div>
      </div>
      {/* Form */}
      <div className="organization-fields">
        {/* Organization Name */}
        <div className="organization-field">
          <label className="organization-label">
            Organization Name <span className="required">*</span>
          </label>

          <input
            ref={organizationNameRef}
            type="text"
            name="organizationName"
            placeholder="Nova Tech"
            className="organization-input"
            value={formik.values.organizationName}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          />

          {formik.touched.organizationName && formik.errors.organizationName && (
            <p className="error-text">{formik.errors.organizationName}</p>
          )}
        </div>

        {/* License Number */}
        <div className="organization-field">
          <label className="organization-label">
            License Number <span className="required">*</span>
          </label>

          <select
            ref={licenseNumberRef}
            name="licenseNumber"
            className="organization-input"
            value={formik.values.licenseNumber}
            onChange={formik.handleChange}
            onBlur={formik.handleBlur}
          >
            <option value="">Select License</option>

            {licenseOptions.map((license) => (
              <option key={license} value={license}>
                {license}
              </option>
            ))}
          </select>

          {formik.touched.licenseNumber && formik.errors.licenseNumber && <p className="error-text">{formik.errors.licenseNumber}</p>}
        </div>
      </div>

      {/* Multi Language */}
      <div className="mt-6 w-[400px]">
        <MultiLanguageSelect value={formik.values.languages} onChange={(codes) => formik.setFieldValue('languages', codes)} />
      </div>

      {/* Question */}
      <div className="organization-type-section">
        <div className="organization-question">
          <label className="organization-question-title">What best describes your organization?</label>
        </div>
        <div className="organization-cards">
          {organizationTypes.map((type: any) => (
            <div
              key={type.id}
              className={`organization-card ${selectedType === type.id ? 'active' : ''}`}
              onClick={() => setSelectedType(type.id)}
            >
              <input type="radio" checked={selectedType === type.id} readOnly className="organization-radio" />

              <div className="organization-card-icon">
                <Settings size={22} />
              </div>

              <h3 className="organization-card-title">{type.title}</h3>

              <p className="organization-card-description">{type.description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Info Box */}
      <div className="flex flex-col mt-8">
        <div className="organization-alert">
          <CircleAlert />
          <span className="organization-alert-text">You can't change your organization setting for later if needed.</span>
        </div>
      </div>

      {/* Buttons */}
      <div className="organization-footer">
        {/* Back */}
        <button onClick={previousStep} className="organization-back-btn">
          <ArrowLeft size={18} />
          <span>Back</span>
        </button>

        <div className="organization-footer-right">
          {/* Save */}
          <button className="organization-save-btn">
            <Save size={16} />
            <span>Save & Edit</span>
          </button>

          {/* Next */}
          <button
            type="button"
            className="organization-next-btn"
            onClick={async () => {
              const errors = await formik.validateForm();

              formik.setTouched({
                organizationName: true,
                licenseNumber: true,
              });

              if (errors.organizationName) {
                organizationNameRef.current?.focus();
                return;
              }

              if (errors.licenseNumber) {
                licenseNumberRef.current?.focus();
                return;
              }

              formik.handleSubmit();
            }}
          >
            <span>Next</span>
            <ArrowRight size={18} />
          </button>
        </div>
      </div>
    </form>
  );
};

export default OrganizationForm;
